/**
 * Type definitions for trAInedup.ai platform
 * Defines all the data structures used throughout the application
 */

// User role enumeration following RBAC matrix from PRD
export enum UserRole {
  STUDENT = 'student',
  INSTRUCTOR = 'instructor',
  ADMIN = 'admin'
}

// Subscription tier enumeration
export enum SubscriptionTier {
  FREE = 'free',
  STANDARD = 'standard',
  PREMIUM = 'premium',
  ANNUAL = 'annual',
  TWELVE_MONTH_PACKAGE = 'twelve_month_package',
  CORPORATE = 'corporate',
  STACKCOMMERCE_LIFETIME = 'stackcommerce_lifetime'
}

// User interface definition
export interface User {
  uid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  photoURL?: string;
  roles: string[];
  subscriptionTier?: SubscriptionTier;
  subscriptionStatus?: 'active' | 'inactive' | 'cancelled' | 'past_due';
  subscriptionEndDate?: any; // Firestore Timestamp for 12-month package expiry
  credits?: number; // Course enrollment credits for 12-month package (50 per year)
  stripeCustomerId?: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  emailVerified: boolean;
  profileComplete: boolean;
  
  // Profile fields
  bio?: string;
  location?: string;
  jobTitle?: string;
  company?: string;
  website?: string;
  linkedIn?: string;
  twitter?: string;
  youtube?: string;
  intendedDestination?: string;
  
  // Skills assessment results
  skillsProfile?: SkillsProfile;
  lastAssessmentDate?: Date;
  assessmentHistory?: Array<{
    assessmentId: string;
    completedAt: Date;
    planId: string;
  }>;
  skillsAssessmentCompleted?: boolean;
  skillsAssessmentCompletedAt?: Date;
  currentLearningPlanId?: string;
  // Onboarding completion status
  onboardingCompleted?: boolean;

  // Activation code metadata
  activationCodeId?: string;
  activationCodeSource?: string;
  activationCodeCampaignId?: string | null;
  activationCodeRedeemedAt?: Date;
  membershipPlan?: string;
  acquisitionSource?: string;
}

// Skills profile from AI assessment
export interface SkillsProfile {
  promptEngineering: number; // 1-5 scale
  criticalEvaluation: number; // 1-5 scale
  dataInterpretation: number; // 1-5 scale
  toolUsage: number; // 1-5 scale
  ethicalConsiderations: number; // 1-5 scale
  assessmentDate: Date;
}

// Course-related interfaces
export interface Course {
  id: string;
  title: string;
  description: string;
  content: string; // Markdown content
  competency: string; // Which skill this course addresses
  level: number; // 1-5 difficulty level
  estimatedDuration: number; // in minutes
  videoUrl?: string;
  downloadableResources?: string[]; // URLs to PDFs, templates, etc.
  tags: string[];
  createdBy: string; // instructor uid
  createdAt: Date;
  updatedAt: Date;
  isPublished: boolean;
}

// User progress tracking
export interface UserProgress {
  userId: string;
  courseId: string;
  completedLessons: string[];
  completionPercentage: number;
  startedAt: Date;
  completedAt?: Date;
  lastAccessedAt: Date;
}

// Learning path recommendation
export interface LearningPath {
  userId: string;
  recommendedCourses: string[]; // Course IDs in recommended order
  currentCourse?: string;
  generatedAt: Date;
  updatedAt: Date;
}

// Comment system for courses
export interface Comment {
  id: string;
  courseId: string;
  userId: string;
  userDisplayName: string; // Denormalized for efficiency
  userPhotoURL?: string; // Denormalized for efficiency
  content: string;
  parentCommentId?: string; // For nested replies
  createdAt: Date;
  updatedAt: Date;
  isModerated: boolean;
}

// Direct messaging system
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderDisplayName: string; // Denormalized
  content: string;
  timestamp: Date;
  readBy: string[]; // Array of user IDs who have read the message
  messageType: 'text' | 'image' | 'file';
  attachmentUrl?: string;
}

// Conversation for messaging
export interface Conversation {
  id: string;
  participants: string[]; // Array of user IDs
  participantDetails: { // Denormalized participant info
    [userId: string]: {
      displayName: string;
      photoURL?: string;
    };
  };
  lastMessage?: {
    content: string;
    timestamp: Date;
    senderId: string;
  };
  isGroupChat: boolean;
  groupName?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Expert consultation booking
export interface ExpertConsultation {
  id: string;
  expertId: string;
  studentId: string;
  scheduledAt: Date;
  duration: number; // in minutes
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  meetingUrl?: string;
  notes?: string;
  createdAt: Date;
}

// Expert profile
export interface Expert {
  id: string;
  userId: string; // Links to User document
  specialties: string[];
  bio: string;
  hourlyRate: number;
  availability: {
    [day: string]: { // 'monday', 'tuesday', etc.
      start: string; // '09:00'
      end: string; // '17:00'
    };
  };
  calendlyUrl?: string;
  isActive: boolean;
}

// Subscription management
export interface Subscription {
  id: string;
  userId: string;
  stripeSubscriptionId: string;
  tier: SubscriptionTier;
  status: 'active' | 'inactive' | 'cancelled' | 'past_due';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Referral system
export interface Referral {
  id: string;
  referrerId: string;
  refereeId?: string; // Set when someone signs up using the referral
  referralCode: string;
  status: 'pending' | 'completed' | 'rewarded';
  rewardAmount?: number;
  createdAt: Date;
  completedAt?: Date;
}

// Analytics and metrics
export interface PlatformMetrics {
  totalUsers: number;
  activeSubscriptions: {
    basic: number;
    premium: number;
    corporate: number;
  };
  monthlyRecurringRevenue: number;
  courseCompletionRate: number;
  averageEngagementTime: number;
  lastUpdated: Date;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Form validation types
export interface ValidationError {
  field: string;
  message: string;
}

// Onboarding chatbot interaction
export interface OnboardingResponse {
  userId: string;
  responses: {
    role?: string;
    goals?: string[];
    currentKnowledge?: string;
    companySize?: string;
  };
  recommendedTier: SubscriptionTier;
  completedAt: Date;
}

// Notification system
export interface Notification {
  id: string;
  userId: string;
  type: 'course_completion' | 'new_message' | 'expert_booking' | 'subscription_update';
  title: string;
  message: string;
  isRead: boolean;
  actionUrl?: string;
  createdAt: Date;
}
